/**
 * O casco do painel do Logigate (`logigate.hongayetu.com/{empresa}`): o `EstruturaPainel` do
 * kit com o menu `window.empresa_navigation`, os números vivos do `/vs`
 * (`navigation_badges`), o seletor da empresa e os seletores dos grupos (escola, hotel,
 * hospital, condomínio, estabelecimento), que trocam os sub-itens desse grupo.
 *
 * Embrulha o `MainProvider` (o `/vs`, o ecrã de bloqueio, a sessão perdida, o Agente Silva)
 * e mostra os avisos e bloqueios do plano e da escola.
 */
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { usePage } from "@inertiajs/react";
import { Icon } from "@iconify/react";
import { EstruturaPainel, ItemMenuServidor, ItemPainel, JanelaNovidades, menuDoServidor, partesDoEndereco, useApresentacaoDoPainel } from "@/Logigate/ui/painel";
import { useTema } from "@/Logigate/ui/tema";
import { EVENTO, subscribe, unsubscribe } from "@/utils/events";
import req from "@/utils/req";
import { NOVIDADES_LOGIGATE, TUTORIAL_LOGIGATE } from "./apresentacaoLogigate";
import MainProvider from "@/Logigate/MainProvider";
import EmpresaSeletor from "@/Logigate/components/layout/EmpresaSeletor";
import EscolaSeletor from "@/Logigate/components/layout/EscolaSeletor";
import HotelSeletor from "@/Logigate/components/layout/HotelSeletor";
import HospitalSeletor from "@/Logigate/components/layout/HospitalSeletor";
import CondominioSeletor from "@/Logigate/components/layout/CondominioSeletor";
import EstabelecimentoSeletor from "@/Logigate/components/layout/EstabelecimentoSeletor";
import UnidadeSeletor from "@/Logigate/components/layout/UnidadeSeletor";
import EscolaBloqueadaOverlay from "@/Logigate/components/EscolaBloqueadaOverlay";
import PlanoBloqueadoBanner from "@/Logigate/components/PlanoBloqueadoBanner";
import PlanoBloqueadoOverlay from "@/Logigate/components/PlanoBloqueadoOverlay";
import OuvinteGuardarNaBiblioteca from "@/Logigate/components/GuardarNaBiblioteca/OuvinteGuardarNaBiblioteca";
import AvisoServicosPagos from "@/components/escola/AvisoServicosPagos";
import AvisoRescisao from "@/components/escola/AvisoRescisao";
import FaixaEliminacaoAgendada, { usePodeCancelarEliminacao } from "@/Logigate/components/construtor/FaixaEliminacaoAgendada";

interface Props {
    children: ReactNode;
    semMargens?: boolean;
}

export function EstruturaLogigate(props: Props) {
    const { url } = usePage();
    return (
        <MainProvider>
            <CascoLogigate {...props} />
            <OuvinteGuardarNaBiblioteca />
            {/* A Escola Inteligente dentro do Logigate: o mesmo aviso do painel da Escola. */}
            {url.includes("/escola-inteligente") && <AvisoServicosPagos />}
        </MainProvider>
    );
}

type Trocador = (submenu: ItemMenuServidor[], escolhidoId: number) => void;

/** O sub-menu trocado por um seletor e o sítio escolhido (escola, hotel…). */
interface Troca {
    menu: ItemMenuServidor[];
    escolhidoId: number;
}

/**
 * Onde cada grupo diz qual é o sítio escolhido. Guardar lá a escolha faz o seletor voltar a
 * montar com ela (ao fechar e abrir o grupo, ou no menu flutuante do menu minimizado), sem
 * pedir outra vez o menu da loja guardada.
 */
const CAMPO_ESCOLHIDO: Record<string, string> = {
    "Escola Inteligente": "escola_selecionada_id",
    "Gestão Hoteleira": "hotel_selecionado_id",
    "Gestão Hospitalar": "hospital_selecionado_id",
    "Gestão Condominial": "condominio_selecionado_id",
    "Humbi Shop": "estabelecimento_selecionado_id",
};

/** O seletor que vai no topo de um grupo, quando há mais do que um sítio para escolher. */
function seletorDoGrupo(bruto: ItemMenuServidor, trocar: Trocador): ReactNode {
    const b = bruto as any;
    const muda = (sitio: { id: number }, menu: ItemMenuServidor[]) => menu && trocar(menu, sitio.id);
    switch (bruto.nome) {
        case "Escola Inteligente":
            return b.escolas?.length > 1 ? <EscolaSeletor escolas={b.escolas} escolaSelecionadaId={b.escola_selecionada_id} onEscolaChange={muda} /> : null;
        case "Gestão Hoteleira":
            return b.hoteis?.length > 1 ? <HotelSeletor hoteis={b.hoteis} hotelSelecionadoId={b.hotel_selecionado_id} onHotelChange={muda} /> : null;
        case "Gestão Hospitalar":
            return b.hospitais?.length > 1 ? <HospitalSeletor hospitais={b.hospitais} hospitalSelecionadoId={b.hospital_selecionado_id} onHospitalChange={muda} /> : null;
        case "Gestão Condominial":
            return b.condominios?.length > 1 ? <CondominioSeletor condominios={b.condominios} condominioSelecionadoId={b.condominio_selecionado_id} onCondominioChange={muda} /> : null;
        case "Humbi Shop":
            return b.estabelecimentos?.length > 1 ? <EstabelecimentoSeletor estabelecimentos={b.estabelecimentos} estabelecimentoSelecionadoId={b.estabelecimento_selecionado_id} onEstabelecimentoChange={muda} /> : null;
        default:
            // Um módulo do Construtor ou de um pacote por unidade (plano 3.20): a lavandaria, a oficina…
            // A `key` volta a montá-lo quando a unidade muda fora dele (uma ligação para outra).
            return b.unidades?.length > 1 && b.unidades_caminho ? (
                <UnidadeSeletor
                    key={b.unidade_selecionada_id ?? "nenhuma"}
                    unidades={b.unidades}
                    unidadeSelecionadaId={b.unidade_selecionada_id}
                    caminho={b.unidades_caminho}
                    rotulo={b.unidade_rotulo || "Unidade"}
                    icone={b.icon}
                    onUnidadeChange={muda}
                />
            ) : null;
    }
}

/**
 * O endereço de um item do primeiro nível. Só do primeiro: dentro dos módulos há outros com o
 * mesmo nome (as «Notificações» do Condomínio são a configuração delas, não as do painel).
 */
function acharUrl(itens: ItemMenuServidor[], nome: string): string | null {
    return itens.find((i) => i?.nome === nome && i.url && !i.submenu?.length)?.url ?? null;
}

function CascoLogigate({ children, semMargens }: Props) {
    const { props, url } = usePage<any>();
    // O modo claro/escuro do kit (`TemaProvider` no app.tsx): a chave `vite-ui-theme`; sem escolha, segue o aparelho.
    const { tema, setTema, escuro } = useTema();
    const [contagens, setContagens] = useState<Record<string, string | number>>({});
    const [totalNotificacoes, setTotalNotificacoes] = useState<number | string>(props.total_n || 0);
    // Os sub-itens trocados por um seletor (nome do grupo → menu novo e sítio escolhido).
    const [submenus, setSubmenus] = useState<Record<string, Troca>>({});

    // O sino conta as novas (por ver). O número vem em todas as visitas (e na própria resposta
    // que abre a lista, já a zero); a sondagem do `/vs` acerta-o entre visitas. Lido só no
    // arranque, o sino ficava aceso depois de se abrir a lista até à sondagem seguinte.
    useEffect(() => setTotalNotificacoes(props.total_n || 0), [props.total_n]);

    useEffect(() => {
        const notifica = (e: any) => {
            const d = e.detail ?? {};
            if (d.total_n !== undefined) {
                setTotalNotificacoes(d.total_n);
            }
            if (d.navigation_badges && typeof d.navigation_badges === "object") {
                setContagens(d.navigation_badges);
            }
        };
        subscribe(EVENTO.NOTIFICA, notifica);
        return () => unsubscribe(EVENTO.NOTIFICA, notifica);
    }, []);

    const brutos: ItemMenuServidor[] = useMemo(() => {
        const base: ItemMenuServidor[] = Array.isArray((window as any).empresa_navigation) ? (window as any).empresa_navigation : [];
        const trocar = (lista: ItemMenuServidor[]): ItemMenuServidor[] =>
            lista.map((i) => {
                const troca = submenus[i.nome];
                if (troca) {
                    const campo = CAMPO_ESCOLHIDO[i.nome] ?? ((i as any).unidades_caminho ? "unidade_selecionada_id" : undefined);
                    return { ...i, submenu: troca.menu, ...(campo ? { [campo]: troca.escolhidoId } : {}) };
                }
                return i.submenu ? { ...i, submenu: trocar(i.submenu) } : i;
            });
        return trocar(base);
    }, [submenus]);

    const itens = useMemo(() => {
        // A chave «Notificações», sem caminho, é a do primeiro nível: as do painel. Vai sempre,
        // mesmo vazia: sem ela o item ficava com o número do menu do arranque (ou da sondagem
        // anterior) depois de o sino já ter descido.
        const todas = { ...contagens, "Notificações": totalNotificacoes || "" };
        const convertidos = menuDoServidor(brutos, todas);
        // O seletor de cada grupo, em paralelo com a árvore do servidor.
        const juntar = (crus: ItemMenuServidor[], novos: ItemPainel[]): ItemPainel[] =>
            novos.map((n, i) => {
                const cru = crus[i];
                if (!cru || !n.itens) {
                    return n;
                }
                return {
                    ...n,
                    antesDosItens: seletorDoGrupo(cru, (menu, escolhidoId) => setSubmenus((s) => ({ ...s, [cru.nome]: { menu, escolhidoId } }))),
                    itens: juntar(cru.submenu ?? [], n.itens),
                };
            });
        return juntar(brutos.filter((i) => i && typeof i === "object" && i.nome), convertidos);
    }, [brutos, contagens, totalNotificacoes]);

    const {
        escola_bloqueada,
        escola_bloqueada_mensagem,
        escola,
        plano_bloqueio,
        modulo_atual,
    } = props;

    // A rescisão do contrato da escola: nas páginas da Escola Inteligente o aviso da escola escolhida
    // (se ela o tiver); no resto do Logigate, o da empresa (se a rescisão o pediu).
    const avisoRescisao = (url.includes("/escola-inteligente") ? props.aviso_rescisao_escola : null) ?? props.aviso_rescisao_empresa ?? null;
    // A eliminação agendada: a da empresa (quando sai com a escola) em todo o Logigate; a da escola nas páginas dela.
    const avisoEliminacao = props.aviso_eliminacao_empresa ?? (url.includes("/escola-inteligente") ? props.aviso_eliminacao_escola : null) ?? null;
    // Um módulo do Construtor com a eliminação agendada: a faixa no topo de todas as páginas dele.
    const moduloAEliminar = props.construtor?.modulo?.eliminacao_agendada_para ? props.construtor.modulo : null;
    const podeCancelarEliminacao = usePodeCancelarEliminacao();

    const moduloBloqueado = !!plano_bloqueio && modulo_atual !== null && modulo_atual !== undefined && !!plano_bloqueio.modulos_afetados?.includes(modulo_atual);
    const mostrarBanner = moduloBloqueado && ["warning", "pendente"].includes(plano_bloqueio?.nivel_bloqueio);
    const mostrarOverlay = moduloBloqueado && plano_bloqueio?.nivel_bloqueio === "blocked";

    const basePath: string = props.base_path || "/";
    const urlNotificacoes = acharUrl(brutos, "Notificações");

    // O menu segue a unidade da página (plano 3.20: a unidade vai no endereço). Uma ligação para outra
    // lavandaria (uma notificação, uma tarefa, a ficha de um registo de outra) troca o sub-menu do
    // módulo e o selector para os dela, como o próprio selector.
    const unidadeDaPagina: number | null = props.construtor?.unidade?.id ?? null;
    const moduloDaPagina: number | null = props.construtor?.modulo?.id ?? null;
    useEffect(() => {
        if (!unidadeDaPagina || !moduloDaPagina) {
            return;
        }
        const item = brutos.find((i: any) => i?.unidades_modulo === moduloDaPagina) as any;
        if (!item?.unidades_caminho || item.unidade_selecionada_id === unidadeDaPagina || !item.unidades?.some((u: any) => u.id === unidadeDaPagina)) {
            return;
        }
        let vivo = true;
        req()
            .get(`${basePath.replace(/\/$/, "")}${item.unidades_caminho}/${unidadeDaPagina}/escolher`)
            .then((r) => {
                if (vivo && r?.data?.estado === "ok") {
                    setSubmenus((s) => ({ ...s, [item.nome]: { menu: r.data.menu, escolhidoId: unidadeDaPagina } }));
                }
            });
        return () => {
            vivo = false;
        };
        // Só quando a página muda de módulo ou de unidade.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unidadeDaPagina, moduloDaPagina]);
    const caminho = partesDoEndereco(url)?.caminho ?? "/";
    const inicio = partesDoEndereco(basePath)?.caminho ?? "/";
    const urls = (window as any).urls ?? {};
    const user = props.auth?.user;

    // As novidades (a quem já usava o painel) e depois o tutorial, uma vez cada; voltam a pedido no
    // menu da conta. Com o painel tapado pelo bloqueio, esperam pela próxima página.
    const apresentacao = useApresentacaoDoPainel({
        pendentes: props.apresentacao,
        marcarVisto: (qual) => void req().post(`${basePath.replace(/\/$/, "")}/apresentacao/${qual}/visto`),
        abrirTutorial: (aoTerminar) =>
            void import("@/Logigate/ui/painel/tutorialDoPainel").then(({ abrirTutorialDoPainel }) => abrirTutorialDoPainel({ nome: user?.nome, textos: TUTORIAL_LOGIGATE, aoTerminar })),
        desligada: Boolean(escola_bloqueada) || mostrarOverlay,
    });

    return (
        <>
        <EstruturaPainel
            chave="logigate"
            cor="logigate"
            marca={{
                logotipo: <img src="/logi/imagens/logo.png" alt="" className="h-6 w-auto" />,
                nome: "Logigate",
                detalhe: props.empresa?.nome,
                href: basePath,
            }}
            itens={itens}
            antesDoMenu={props.empresa ? <EmpresaSeletor empresaAtual={props.empresa} /> : undefined}
            utilizador={user ? { nome: user.nome, email: user.email, foto: user.foto_link } : null}
            menuUtilizador={[
                { rotulo: "Minha conta", icone: <Icon icon="mdi:account-circle-outline" />, href: urls.conta, externo: true, oculto: !urls.conta },
                { rotulo: "Ajuda", icone: <Icon icon="mdi:help-circle-outline" />, href: urls.ajuda, externo: true, oculto: !urls.ajuda },
                { rotulo: "Novidades", icone: <Icon icon="mdi:creation-outline" />, onSelect: apresentacao.abrirNovidades },
                { rotulo: "Ver o tutorial", icone: <Icon icon="mdi:map-marker-path" />, onSelect: apresentacao.verTutorial },
            ]}
            // O `/sair` da conta só termina a sessão com `t` (o csrf da sessão partilhada) ou `hash`.
            sair={urls.conta ? { href: `${urls.conta}/sair?t=${encodeURIComponent((window as any).token ?? "")}` } : null}
            notificacoes={urlNotificacoes ? { href: urlNotificacoes, total: totalNotificacoes } : null}
            barraInferior={[
                { chave: "inicio", rotulo: "Início", icone: "mdi:view-dashboard-outline", href: basePath, activo: caminho === inicio },
                { chave: "pesquisar", rotulo: "Pesquisar", icone: "mdi:magnify", abrePesquisa: true },
                ...(urlNotificacoes ? [{ chave: "avisos", rotulo: "Avisos", icone: "mdi:bell-outline", href: urlNotificacoes, activo: caminho === partesDoEndereco(urlNotificacoes)?.caminho, contagem: totalNotificacoes || null }] : []),
            ]}
            tema={{ escolhido: tema, escuro, mudar: setTema }}
            semMargens={semMargens}
            avisos={
                avisoEliminacao || avisoRescisao || moduloAEliminar || (mostrarBanner && !escola_bloqueada) ? (
                    <>
                        {avisoEliminacao && <AvisoRescisao aviso={avisoEliminacao} />}
                        {moduloAEliminar && <FaixaEliminacaoAgendada modulo={moduloAEliminar} raiz={`${(props.base_path || "").replace(/\/$/, "")}/construtor`} podeCancelar={podeCancelarEliminacao} />}
                        {avisoRescisao && <AvisoRescisao aviso={avisoRescisao} />}
                        {mostrarBanner && !escola_bloqueada && (
                            <PlanoBloqueadoBanner
                                nivel={plano_bloqueio.nivel_bloqueio}
                                diasVencido={plano_bloqueio.dias_vencido}
                                mensagem={plano_bloqueio.mensagem}
                                facturaCodigo={plano_bloqueio.factura_codigo}
                                urlPagamento={plano_bloqueio.url_pagamento}
                                planoAtivo={plano_bloqueio.plano_ativo}
                                diasParaVencer={plano_bloqueio.dias_para_vencer}
                            />
                        )}
                    </>
                ) : undefined
            }
        >
            {escola_bloqueada ? (
                <EscolaBloqueadaOverlay escola={escola} mensagem={escola_bloqueada_mensagem} />
            ) : mostrarOverlay ? (
                <PlanoBloqueadoOverlay mensagem={plano_bloqueio.mensagem} nivel_bloqueio={plano_bloqueio.nivel_bloqueio} plano_bloqueio={plano_bloqueio} />
            ) : (
                children
            )}
        </EstruturaPainel>

        <JanelaNovidades aberta={apresentacao.novidadesAbertas} onFechar={apresentacao.fecharNovidades} produto="Logigate" diapositivos={NOVIDADES_LOGIGATE} />
        </>
    );
}
